import React from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import ExportReportModal from '../../components/modals/ExportReportModal.tsx';
import ScheduleReportModal from '../../components/modals/ScheduleReportModal.tsx';

// Mock data for reports based on the image
const reportsData = [
  { id: 1, icon: '$', title: 'Payout Summary', description: 'Comprehensive summary of all payouts made to merchants, including transaction details, fees, and settlement dates.', frequency: 'Daily', lastGenerated: '2 days ago', format: 'Excel' },
  { id: 2, icon: '$', title: 'Revenue Report', description: 'Detailed breakdown of revenue streams by product category, partner channel, and geographical region.', frequency: 'Weekly', lastGenerated: '1 week ago', format: 'Excel' },
  { id: 3, icon: '%', title: 'Tax Report', description: 'Detailed VAT and tax breakdown for financial compliance, reconciliation, and regulatory reporting.', frequency: 'Monthly', lastGenerated: '3 days ago', format: 'PDF' },
  { id: 4, icon: 'P', title: 'Partner Performance', description: 'Comprehensive analysis of partner metrics including transaction volume, conversion rates, and revenue contribution.', frequency: 'Monthly', lastGenerated: '2 weeks ago', format: 'Excel' },
  { id: 5, icon: '$', title: 'Financial Statement', description: 'Comprehensive financial statement with balance sheet, income statement, and cash flow analysis for accounting purposes.', frequency: 'Quarterly', lastGenerated: '1 month ago', format: 'PDF' },
  { id: 6, icon: '📊', title: 'Transaction Reconciliation', description: 'Detailed transaction reconciliation report across all payment systems with exception highlights and resolution status.', frequency: 'Daily', lastGenerated: '1 day ago', format: 'CSV' },
  { id: 7, icon: '%', title: 'Risk Assessment', description: 'In-depth analysis of risk factors, potential fraud indicators, and security compliance metrics.', frequency: 'Weekly', lastGenerated: '5 days ago', format: 'PDF' },
  { id: 8, icon: 'P', title: 'Partner Settlement', description: 'Detailed record of partner settlements including commission structure, fees, adjustments, and payment status.', frequency: 'Weekly', lastGenerated: '4 days ago', format: 'Excel' },
  { id: 9, icon: '📊', title: 'Product Performance', description: 'Comprehensive analysis of key performance indicators by product line, including sales trends and profitability metrics.', frequency: 'Monthly', lastGenerated: '1 week ago', format: 'Excel' },
  { id: 10, icon: '%', title: 'Audit Log', description: 'Chronological record of all system actions and transactions for compliance and security audit purposes.', frequency: 'On Demand', lastGenerated: '3 days ago', format: 'CSV' },
];

// Updated helper to get badge styles aligning with Merchant Wallet visual
const getBadgeStyle = (format: string): React.CSSProperties => {
  // Common badge styles
  const baseStyle: React.CSSProperties = {
    padding: '3px 10px', // Slightly adjusted padding
    borderRadius: '12px', // More pill-like
    fontSize: '12px',
    fontWeight: 500,
    border: '1px solid transparent', // Keep border definition, specific colors below
    display: 'inline-block',
    lineHeight: '1.5',
  };

  switch (format.toUpperCase()) {
    // Green for Excel (like 'Credit' badge)
    case 'EXCEL': return { ...baseStyle, backgroundColor: '#eefbf3', color: '#15803d', borderColor: '#bbf7d0' }; // Light green bg, dark green text
    // Red for PDF (like 'Debit' badge)
    case 'PDF': return { ...baseStyle, backgroundColor: '#fff1f2', color: '#be123c', borderColor: '#fecaca' };   // Light red bg, dark red text
    // Blue for CSV (like 'Manual Corrections' badge)
    case 'CSV': return { ...baseStyle, backgroundColor: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe' };   // Light blue/indigo bg, dark blue/indigo text
    // Default Gray
    default: return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' };
  }
};

const ReportsPage: React.FC = () => {
  // State for modal visibility and selected report ID
  const [isExportModalOpen, setIsExportModalOpen] = React.useState<boolean>(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = React.useState<boolean>(false);
  const [selectedReportId, setSelectedReportId] = React.useState<number | null>(null);

  // Categories based on the image
  const categories = ['All Reports', 'Financial', 'Operational', 'Compliance', 'Partner'];

  // Common button style based on Merchant Wallet filters/actions
  const buttonBaseStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db', // Gray border
    borderRadius: '6px',
    fontSize: '14px',
    color: '#374151', // Dark gray text
    cursor: 'pointer',
    lineHeight: '1.5',
  };

  // Style for active category filter button
  const activeCategoryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle, // Inherit base style
    padding: '6px 12px', // Specific padding for category pills
    borderRadius: '16px', // Pill shape
    borderColor: '#a5b4fc', // Indigo border
    backgroundColor: '#eef2ff', // Light indigo background
    color: '#4338ca', // Indigo text
    fontWeight: 500,
  };

  // Style for inactive category filter button
  const inactiveCategoryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle, // Inherit base style
    padding: '6px 12px', // Specific padding for category pills
    borderRadius: '16px', // Pill shape
    borderColor: '#e5e7eb', // Lighter gray border
    backgroundColor: 'white',
    color: '#4b5563', // Medium gray text
    fontWeight: 500,
    fontSize: '13px',
  };

  // Style for card action buttons (Generate/Schedule)
  const cardActionButtonStyle: React.CSSProperties = {
      ...buttonBaseStyle, // Inherit base style
      flex: 1, // Make buttons fill space
      textAlign: 'center',
      padding: '8px 12px',
      fontSize: '14px',
      fontWeight: 500, // Slightly bolder
  };

  // Style for Report Card Icon
  const reportIconStyle: React.CSSProperties = {
    width: '36px', // Slightly larger
    height: '36px',
    borderRadius: '8px', // Match card radius
    backgroundColor: '#f3f4f6', // Neutral gray background
    color: '#4b5563', // Neutral gray icon color
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px', // Slightly larger icon size
    fontWeight: '600', // Bolder icon
  };

  // Handlers for modals
  const openExportModal = (reportId: number | null = null) => {
    setSelectedReportId(reportId);
    setIsExportModalOpen(true);
    setIsScheduleModalOpen(false);
  };
  const openScheduleModal = (reportId: number | null = null) => {
    setSelectedReportId(reportId);
    setIsScheduleModalOpen(true);
    setIsExportModalOpen(false);
  };
  const closeModal = () => {
    setIsExportModalOpen(false);
    setIsScheduleModalOpen(false);
    setSelectedReportId(null);
  };

  // Find the selected report title
  const selectedReportTitle = React.useMemo(() => {
    if (selectedReportId === null) {
      return 'Custom Report'; // Default title for top Export button
    }
    const report = reportsData.find(r => r.id === selectedReportId);
    return report ? report.title : 'Custom Report'; // Use report title or default
  }, [selectedReportId]);

  return (
    <MainLayout>
      <div style={{ padding: '24px', minHeight: '100vh' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Reports</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Generate, schedule, and manage financial and operational reports</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          {reportsData.map((report) => (
            <div key={report.id} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <div style={reportIconStyle}>
                    {report.icon === '|||' ? '📊' : report.icon}
                  </div>
                  <span style={getBadgeStyle(report.format)}>
                    {report.format}
                  </span>
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>{report.title}</h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>{report.description}</p>
                <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                  <span>Frequency: <span style={{color: '#374151', fontWeight: 500}}>{report.frequency}</span></span>
                  <span>Last generated: <span style={{color: '#374151', fontWeight: 500}}>{report.lastGenerated}</span></span>
                </div>
              </div>
              <div style={{ display: 'flex', borderTop: '1px solid #e5e7eb', paddingTop: '16px', gap: '12px' }}>
                <button style={cardActionButtonStyle} onClick={() => openExportModal(report.id)}>
                  Generate
                </button>
                <button style={cardActionButtonStyle} onClick={() => openScheduleModal(report.id)}>
                  Schedule
                </button>
              </div>
            </div>
          ))}
        </div>

      </div>
      {isExportModalOpen && (
        <ExportReportModal onClose={closeModal} reportTitle={selectedReportTitle} />
      )}
      {isScheduleModalOpen && (
        <ScheduleReportModal onClose={closeModal} reportTitle={selectedReportTitle} />
      )}
    </MainLayout>
  );
};

export default ReportsPage;
